import {
  useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent,
} from "react";
import { ChevronDown, X as XIcon } from "lucide-react";

/* ──────────────────────────────────────────────────────────────────────
   <Combobox> — typeable autocomplete bound to a fixed option list.

   - Substring match against `label` (case-insensitive, diacritic-naive).
   - Free-text allowed: user can type a value not in the list. We always
     emit what they typed, even if it doesn't match any option.
   - Keyboard: ↑ / ↓ to walk, Enter to commit highlighted, Esc to close.
   - Renders an optional "hint" (e.g. "Karnataka" next to "Bengaluru").

   No external dependencies beyond lucide-react.
   ────────────────────────────────────────────────────────────────────── */

export interface ComboOption {
  /** Value that gets emitted to the parent on selection. */
  value: string;
  /** Primary text shown in the dropdown. Also what we substring-search. */
  label: string;
  /** Optional smaller right-aligned hint (e.g. state name beside a city). */
  hint?: string;
  /** Optional pill-style count (e.g. edition_count for a city). */
  count?: number;
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<ComboOption>;
  placeholder?: string;
  emptyHint?: string;
  /** Search inside `hint` too, so "kar" matches Bengaluru (state=Karnataka). */
  searchHint?: boolean;
  /** Disable clearing — keep the small × button hidden. */
  noClear?: boolean;
  className?: string;
  /** Max items rendered (perf cap on large lists like cities=166). */
  maxItems?: number;
  /** Accessibility */
  ariaLabel?: string;
}

export function Combobox({
  value, onChange,
  options,
  placeholder,
  emptyHint = "No match",
  searchHint = false,
  noClear,
  className,
  maxItems = 60,
  ariaLabel,
}: Props) {
  const [open, setOpen]   = useState(false);
  const [hi,   setHi]     = useState(0);
  const [draft, setDraft] = useState(value);    // what's typed in the input
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /* If the parent updates `value` externally, mirror it into draft. */
  useEffect(() => { setDraft(value); }, [value]);

  /* Close on outside click. */
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  /* Filter. */
  const filtered = useMemo(() => {
    const q = draft.trim().toLowerCase();
    if (!q) return options.slice(0, maxItems);
    return options.filter((o) => {
      if (o.label.toLowerCase().includes(q)) return true;
      if (searchHint && o.hint && o.hint.toLowerCase().includes(q)) return true;
      return false;
    }).slice(0, maxItems);
  }, [draft, options, searchHint, maxItems]);

  const commit = useCallback((v: string) => {
    setDraft(v);
    onChange(v);
    setOpen(false);
  }, [onChange]);

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi(h => Math.min(filtered.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi(h => Math.max(0, h - 1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (open && filtered[hi]) commit(filtered[hi].value);
      else { onChange(draft); setOpen(false); }
    }
    else if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
    else if (e.key === "Tab") { setOpen(false); }
  };

  return (
    <div ref={ref} className={`relative ${className ?? ""}`}>
      <div className="flex items-center"
           style={{ borderBottom: "1px solid var(--rule-strong)" }}>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          aria-label={ariaLabel}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => { setDraft(e.target.value); onChange(e.target.value); setOpen(true); setHi(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          className="flex-1 bg-transparent py-2 px-0 text-[14px] font-mono outline-none"
        />
        {!noClear && draft && (
          <button
            type="button"
            onClick={() => { setDraft(""); onChange(""); inputRef.current?.focus(); }}
            className="text-[var(--fg-faint)] hover:text-[var(--fg)] p-1"
            aria-label="Clear"
          >
            <XIcon size={11} />
          </button>
        )}
        <button
          type="button"
          tabIndex={-1}
          onClick={() => { setOpen(o => !o); inputRef.current?.focus(); }}
          className="text-[var(--fg-faint)] hover:text-[var(--fg)] p-1"
          aria-label="Open list"
        >
          <ChevronDown size={12} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
        </button>
      </div>

      {open && (
        <ul
          role="listbox"
          className="absolute z-30 left-0 right-0 mt-1 max-h-72 overflow-auto np-card shadow-plate"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 font-mono text-[11px] text-[var(--fg-faint)]">
              {emptyHint}
            </li>
          ) : filtered.map((o, i) => (
            <li
              key={`${o.value}-${i}`}
              role="option"
              aria-selected={i === hi}
              onMouseEnter={() => setHi(i)}
              onMouseDown={(e) => { e.preventDefault(); commit(o.value); }}
              className="px-3 py-1.5 flex items-baseline justify-between gap-2 cursor-pointer"
              style={{
                background: i === hi ? "var(--bg-inset)" : "transparent",
                color: i === hi ? "var(--fg)" : "var(--fg-muted)",
                borderLeft: i === hi ? "2px solid var(--accent)" : "2px solid transparent",
              }}
            >
              <span className="font-display text-[13px] truncate">{o.label}</span>
              {o.hint && (
                <span className="font-mono text-[10px] text-[var(--fg-faint)] truncate ml-auto">
                  {o.hint}
                </span>
              )}
              {o.count !== undefined && (
                <span className="font-mono text-[10px] text-[var(--fg-faint)] tabular-nums ml-2">
                  {o.count}
                </span>
              )}
            </li>
          ))}
          {options.length > filtered.length && draft && (
            <li className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] border-t"
                style={{ borderColor: "var(--rule)" }}>
              showing first {filtered.length} of {options.length}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
